import type {DoppeViewerLang} from "../doppe-viewer-lang";
import type {DateTimeValue} from '@wix/devzai-utils-common';
import {dateResolveFromDateTimeValue} from '@wix/devzai-utils-common';

/**
 * Options for controlling timezone display in date formatting.
 */
export interface DoppeViewerDateTimeFormatTimeZoneOptions {
    /** Whether to display the time zone abbreviation (e.g., "GMT+3"). Retrieved from the Wix Event's showTimeZone setting. */
    showTimeZone?: boolean;
    /** Time zone ID in TZ database format (e.g., "America/New_York"). When provided, the date is formatted in this timezone. */
    timeZoneId?: string | null;
}

export function doppeViewerIntlDateTimeFormatShortMonthAndDay (
    doppeViewerLang: DoppeViewerLang,
    date: Date | number
) {
    return Intl.DateTimeFormat(doppeViewerLang, {month: 'short', day: 'numeric'}).format(date)
}

export function doppeViewerIntlDateTimeFormatDateString (
    doppeViewerLang: DoppeViewerLang,
    date: Date | number,
    timeZoneOptions?: DoppeViewerDateTimeFormatTimeZoneOptions
) {
    const options: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        ...(timeZoneOptions?.timeZoneId ? {timeZone: timeZoneOptions.timeZoneId} : {}),
        ...(timeZoneOptions?.showTimeZone ? {timeZoneName: 'short'} : {}),
    };

    return Intl.DateTimeFormat(doppeViewerLang, options).format(new Date(date))
}

// Internal versions (picked by hopp day picker) use UTC, This is the format the date is stored and displayed in the UI. day is UTC midnight.
export function doppeViewerIntlDateTimeFormatDateStringInternal (
    doppeViewerLang: DoppeViewerLang,
    date: Date | number
) {
    return Intl.DateTimeFormat(doppeViewerLang, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(date))
}

export function doppeViewerIntlDateTimeFormatShortMonthAndDayInternal (
    doppeViewerLang: DoppeViewerLang,
    date: Date | number
) {
    return Intl.DateTimeFormat(doppeViewerLang, {month: 'short', day: 'numeric', timeZone: 'UTC'}).format(new Date(date))
}

export function doppeViewerIntlDateTimeFormatDateStringFromSource (
    doppeViewerLang: DoppeViewerLang,
    dateTimeValue: DateTimeValue,
    isExternalSource: boolean,
    timeZoneOptions?: DoppeViewerDateTimeFormatTimeZoneOptions
) {
    const resolvedDate = dateResolveFromDateTimeValue(dateTimeValue);
    return isExternalSource
        ? doppeViewerIntlDateTimeFormatDateString(doppeViewerLang, resolvedDate, timeZoneOptions)
        : doppeViewerIntlDateTimeFormatDateStringInternal(doppeViewerLang, resolvedDate);
}

/**
 * Formats a time string from a Date, optionally in a specific timezone.
 * Example outputs: "9:00 PM", "21:00" (locale-dependent).
 */
export function doppeViewerIntlDateTimeFormatTimeString (
    doppeViewerLang: DoppeViewerLang,
    date: Date | number,
    timeZoneId?: string | null
) {
    const options: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        ...(timeZoneId ? {timeZone: timeZoneId} : {}),
    };

    return Intl.DateTimeFormat(doppeViewerLang, options).format(new Date(date))
}

/**
 * Formats the time portion of an event start date string, respecting the event's timezone.
 * Returns a formatted time string (e.g., "9:00 PM") or null if the date is not available.
 */
export function doppeViewerIntlFormatEventTimeFromStartDate (
    dateString: string | null,
    timeZoneId?: string | null,
    lang: DoppeViewerLang = 'en'
): string | null {
    if (!dateString) {
        return null;
    }

    return doppeViewerIntlDateTimeFormatTimeString(lang, new Date(dateString), timeZoneId);
}

export function doppeViewerIntlDateTimeFormatShortMonthAndDayFromSource (
    doppeViewerLang: DoppeViewerLang,
    dateTimeValue: DateTimeValue,
    isExternalSource: boolean
) {
    const resolvedDate = dateResolveFromDateTimeValue(dateTimeValue);
    return isExternalSource
        ? doppeViewerIntlDateTimeFormatShortMonthAndDay(doppeViewerLang, resolvedDate)
        : doppeViewerIntlDateTimeFormatShortMonthAndDayInternal(doppeViewerLang, resolvedDate);
}